import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, HStack, Textarea, Text, Badge } from "@chakra-ui/react";
import {
  useAdmissionDocumentation,
  useSaveAdmissionDocumentation,
} from "../../apis/patients";

type Props = {
  admissionId: string;
  initialSeed?: string; // optional seed from AppointmentDetails / PatientAdmissionCard
};

export default function AdmissionDocumentationEditor({
  admissionId,
  initialSeed,
}: Props) {
  const trimmedSeed = (initialSeed || "").trim();
  const hasSeed = trimmedSeed.length > 0;

  // Load existing doc (if any)
  const {
    data: existingDoc,
    isLoading,
    isError,
    error,
  } = useAdmissionDocumentation(admissionId);

  // Save mutation
  const { mutateAsync: saveDoc, isPending: isSaving } =
    useSaveAdmissionDocumentation(admissionId);

  // Local editor state
  const [content, setContent] = useState("");

  // When admissionId / existingDoc / seed change, decide what to show
  useEffect(() => {
    // 1) Existing doc from backend always wins
    if (existingDoc && typeof (existingDoc as any).content === "string") {
      setContent((existingDoc as any).content || "");
      return;
    }

    // 2) No doc yet: if we have a seed, prefill with it
    if (!existingDoc && hasSeed) {
      setContent(trimmedSeed);
      return;
    }

    // 3) No doc, no seed: start empty
    if (!existingDoc && !hasSeed) {
      setContent("");
    }
  }, [existingDoc, hasSeed, trimmedSeed, admissionId]);

  const seedBadge = useMemo(() => {
    if (existingDoc) {
      return (
        <Badge ml={2} colorScheme="green">
          DOC: EXISTS
        </Badge>
      );
    }
    if (hasSeed) {
      return <Badge ml={2}>SEED: {trimmedSeed.length}</Badge>;
    }
    return <Badge ml={2}>SEED: NONE</Badge>;
  }, [existingDoc, hasSeed, trimmedSeed.length]);

  const handleSave = async () => {
    const body = content.trim();
    if (!body) return;
    await saveDoc(body);
  };

  return (
    <Box p={4} borderWidth="1px" borderRadius="md" bg="white">
      <HStack justify="space-between" mb={1}>
        <Text fontWeight="bold">Admission Documentation</Text>
        {seedBadge}
      </HStack>

      <Text fontSize="xs" color="gray.500" mb={2}>
        seedLength={hasSeed ? trimmedSeed.length : 0} • admissionId=
        {admissionId} • doc={existingDoc ? "exists" : "none"} • error=
        {isError ? "yes" : "no"}
      </Text>

      <Textarea
        isDisabled={isLoading}
        placeholder="Enter admission documentation..."
        minH="200px"
        value={content}
        onChange={(e) => setContent(e.target.value)}
      />

      <HStack mt={3} spacing={3}>
        <Button
          size="sm"
          colorScheme="teal"
          onClick={handleSave}
          isLoading={isSaving}
          isDisabled={isLoading || !content.trim()}
        >
          Save
        </Button>
      </HStack>

      {isError && (
        <Text mt={2} fontSize="xs" color="red.500">
          {(error as any)?.message || "Failed to load documentation"}
        </Text>
      )}
    </Box>
  );
}
